import React, { useState, useMemo } from "react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  MessageSquare,
  Check,
  X,
  Minus,
  ChevronDown,
  ChevronRight,
  Building2,
} from "lucide-react";
import {
  AttendanceGridItem,
  DayStatus,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/types";
import AttendanceCard from "./AttendanceCard";
import AttendeeDetailDrawer from "./AttendeeDetailDrawer";

type StatusColumn = "pending" | "present" | "no_show";

interface CardEntry {
  item: AttendanceGridItem;
  dayStatus: DayStatus;
}

interface CompanyGroup {
  companyName: string;
  cards: CardEntry[];
}

const COLUMNS: {
  id: StatusColumn;
  label: string;
  accent: string;
  icon: React.ReactNode;
}[] = [
  {
    id: "pending",
    label: "Pending",
    accent: "bg-gray-200 dark:bg-gray-700",
    icon: <Minus className="h-3 w-3 text-gray-400" />,
  },
  {
    id: "present",
    label: "Present",
    accent: "bg-green-200 dark:bg-green-800",
    icon: <Check className="h-3 w-3 text-green-600 dark:text-green-400" />,
  },
  {
    id: "no_show",
    label: "No Show",
    accent: "bg-red-200 dark:bg-red-800",
    icon: <X className="h-3 w-3 text-red-600 dark:text-red-400" />,
  },
];

interface AttendanceKanbanProps {
  gridItems: AttendanceGridItem[];
  selectedDay: string | null;
  updateAttendance: (
    attendanceId: number,
    data: { status?: string; notes?: string },
  ) => Promise<void>;
  isSessionFinalized?: boolean;
}

function DroppableColumn({
  id,
  label,
  accent,
  count,
  children,
}: {
  id: string;
  label: string;
  accent: string;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      className={`flex flex-col rounded-lg bg-gray-100 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 min-h-[200px] ${
        isOver ? "ring-2 ring-gray-300 dark:ring-slate-600" : ""
      }`}
      ref={setNodeRef}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <div className={`w-2 h-2 rounded-full ${accent}`} />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {label}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          ({count})
        </span>
      </div>
      <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto max-h-[calc(100vh-300px)]">
        {children}
      </div>
    </div>
  );
}

function CompanyGroupHeader({
  companyName,
  cardCount,
  isCollapsed,
  onToggle,
  onMoveAll,
  isFinalized,
  columnId,
}: {
  companyName: string;
  cardCount: number;
  isCollapsed: boolean;
  onToggle: () => void;
  onMoveAll: (status: StatusColumn) => void;
  isFinalized: boolean;
  columnId: StatusColumn;
}) {
  return (
    <div className="flex items-center gap-1.5 py-1.5 px-1">
      <button
        className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
        onClick={onToggle}
        type="button"
      >
        {isCollapsed ? (
          <ChevronRight className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        )}
        <Building2 className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300 truncate">
          {companyName}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
          ({cardCount})
        </span>
      </button>
      {!isFinalized &&
        (() => {
          const next: StatusColumn =
            columnId === "pending"
              ? "present"
              : columnId === "present"
                ? "no_show"
                : "pending";
          const labels: Record<StatusColumn, string> = {
            present: "All present",
            no_show: "All no show",
            pending: "All pending",
          };
          const colors: Record<StatusColumn, string> = {
            present:
              "text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300",
            no_show:
              "text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300",
            pending:
              "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300",
          };
          return (
            <button
              className={`text-[10px] ${colors[next]} transition-colors flex-shrink-0 px-1`}
              onClick={(e) => {
                e.stopPropagation();
                onMoveAll(next);
              }}
              title={`Move all from ${companyName} to ${labels[next]}`}
              type="button"
            >
              {labels[next]}
            </button>
          );
        })()}
    </div>
  );
}

/** Group cards by company. Solo attendees (null/empty company) are ungrouped. */
function groupByCompany(cards: CardEntry[]): {
  groups: CompanyGroup[];
  ungrouped: CardEntry[];
} {
  const map = new Map<string, CardEntry[]>();
  const ungrouped: CardEntry[] = [];

  for (const card of cards) {
    const company = card.item.companyName?.trim();
    if (!company) {
      ungrouped.push(card);
      continue;
    }
    if (!map.has(company)) {
      map.set(company, []);
    }
    map.get(company)!.push(card);
  }

  // Companies with only 1 person go to ungrouped
  const groups: CompanyGroup[] = [];
  map.forEach((companyCards, companyName) => {
    groups.push({ companyName, cards: companyCards });
  });

  groups.sort((a, b) => a.companyName.localeCompare(b.companyName));

  return { groups, ungrouped };
}

export default function AttendanceKanban({
  gridItems,
  selectedDay,
  updateAttendance,
  isSessionFinalized = false,
}: AttendanceKanbanProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [drawerTarget, setDrawerTarget] = useState<{
    item: AttendanceGridItem;
    dayStatus: DayStatus;
  } | null>(null);
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(
    new Set(),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  // Group items by their day status for the selected day
  const columns = useMemo(() => {
    const grouped: Record<StatusColumn, CardEntry[]> = {
      pending: [],
      present: [],
      no_show: [],
    };

    for (const item of gridItems) {
      const ds = selectedDay
        ? item.dayStatuses.find((d) => d.date === selectedDay)
        : null;
      if (!ds) continue;
      grouped[ds.status].push({ item, dayStatus: ds });
    }

    return grouped;
  }, [gridItems, selectedDay]);

  // Company-grouped columns
  const groupedColumns = useMemo(() => {
    const result: Record<
      StatusColumn,
      { groups: CompanyGroup[]; ungrouped: CardEntry[] }
    > = {
      pending: groupByCompany(columns.pending),
      present: groupByCompany(columns.present),
      no_show: groupByCompany(columns.no_show),
    };
    return result;
  }, [columns]);

  const findCurrentStatus = (attendanceIdStr: string): StatusColumn | null => {
    for (const col of COLUMNS) {
      if (
        columns[col.id].some(
          (c) => c.dayStatus.attendanceId.toString() === attendanceIdStr,
        )
      ) {
        return col.id;
      }
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const overIdStr = over.id.toString();

    let targetStatus: StatusColumn | undefined = COLUMNS.find(
      (col) => col.id === overIdStr,
    )?.id;

    if (!targetStatus) {
      for (const col of COLUMNS) {
        if (
          columns[col.id].some(
            (c) => c.dayStatus.attendanceId.toString() === overIdStr,
          )
        ) {
          targetStatus = col.id;
          break;
        }
      }
    }

    if (!targetStatus) return;

    const currentStatus = findCurrentStatus(active.id.toString());
    if (currentStatus === targetStatus) return;

    await updateAttendance(Number(active.id), { status: targetStatus });
  };

  // Click cycles: pending → present → no_show → pending
  const handleCycleStatus = async (
    attendanceId: number,
    currentStatus: string,
  ) => {
    let newStatus: string;
    if (currentStatus === "pending") {
      newStatus = "present";
    } else if (currentStatus === "present") {
      newStatus = "no_show";
    } else {
      newStatus = "pending";
    }
    await updateAttendance(attendanceId, { status: newStatus });
  };

  const handleOpenDetail = (item: AttendanceGridItem, dayStatus: DayStatus) => {
    setDrawerTarget({ item, dayStatus });
  };

  const handleMoveCompany = async (
    companyCards: CardEntry[],
    targetStatus: StatusColumn,
  ) => {
    const promises = companyCards.map((c) =>
      updateAttendance(c.dayStatus.attendanceId, { status: targetStatus }),
    );
    await Promise.all(promises);
  };

  const toggleCompanyCollapse = (key: string) => {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderColumnContent = (colId: StatusColumn) => {
    const { groups, ungrouped } = groupedColumns[colId];
    const allCards = columns[colId];

    if (allCards.length === 0) {
      return (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-8">
          No attendees
        </p>
      );
    }

    // Collect all IDs for sortable context (including grouped ones)
    const allIds = allCards.map((c) => c.dayStatus.attendanceId.toString());

    return (
      <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
        {/* Company groups */}
        {groups.map((group) => {
          const collapseKey = `${colId}:${group.companyName}`;
          const isCollapsed = collapsedCompanies.has(collapseKey);

          return (
            <div key={group.companyName}>
              <CompanyGroupHeader
                cardCount={group.cards.length}
                columnId={colId}
                companyName={group.companyName}
                isCollapsed={isCollapsed}
                isFinalized={isSessionFinalized}
                onMoveAll={(status) => handleMoveCompany(group.cards, status)}
                onToggle={() => toggleCompanyCollapse(collapseKey)}
              />
              {!isCollapsed && (
                <div className="space-y-1.5 ml-3">
                  {group.cards.map(({ item, dayStatus }) => (
                    <AttendanceCard
                      dayStatus={dayStatus}
                      isFinalized={isSessionFinalized}
                      item={item}
                      key={dayStatus.attendanceId}
                      onCycleStatus={handleCycleStatus}
                      onOpenDetail={handleOpenDetail}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Ungrouped cards */}
        {ungrouped.map(({ item, dayStatus }) => (
          <AttendanceCard
            dayStatus={dayStatus}
            isFinalized={isSessionFinalized}
            item={item}
            key={dayStatus.attendanceId}
            onCycleStatus={handleCycleStatus}
            onOpenDetail={handleOpenDetail}
          />
        ))}
      </SortableContext>
    );
  };

  return (
    <>
      <DndContext
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        <div className="grid grid-cols-3 gap-4">
          {COLUMNS.map((col) => (
            <DroppableColumn
              accent={col.accent}
              count={columns[col.id].length}
              id={col.id}
              key={col.id}
              label={col.label}
            >
              {renderColumnContent(col.id)}
            </DroppableColumn>
          ))}
        </div>
        <DragOverlay>
          {activeId
            ? (() => {
                for (const col of COLUMNS) {
                  const found = columns[col.id].find(
                    (c) => c.dayStatus.attendanceId.toString() === activeId,
                  );
                  if (found) {
                    return (
                      <div className="p-3 bg-white dark:bg-slate-800 rounded-md border border-gray-200 dark:border-slate-600 shadow-lg cursor-grabbing w-full">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {found.item.attendee?.fullName || "Unknown"}
                          </p>
                          {found.item.specialRequests && (
                            <MessageSquare className="h-3 w-3 text-amber-500 dark:text-amber-400 flex-shrink-0 ml-2" />
                          )}
                        </div>
                        {found.item.companyName && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                            {found.item.companyName}
                          </p>
                        )}
                        {found.item.specialRequests && (
                          <p className="text-xs mt-1.5 line-clamp-2">
                            <span className="font-medium text-amber-600 dark:text-amber-400">
                              Special Request:
                            </span>{" "}
                            <span className="text-amber-700 dark:text-amber-400/80">
                              {found.item.specialRequests}
                            </span>
                          </p>
                        )}
                        {found.dayStatus.notes && (
                          <p className="text-xs mt-1 line-clamp-2">
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                              Note:
                            </span>{" "}
                            <span className="text-gray-400 dark:text-gray-500">
                              {found.dayStatus.notes}
                            </span>
                          </p>
                        )}
                      </div>
                    );
                  }
                }
                return null;
              })()
            : null}
        </DragOverlay>
      </DndContext>

      <AttendeeDetailDrawer
        dayStatus={drawerTarget?.dayStatus ?? null}
        isFinalized={isSessionFinalized}
        item={drawerTarget?.item ?? null}
        onClose={() => setDrawerTarget(null)}
        onSaveNotes={async (attendanceId, notes) => {
          await updateAttendance(attendanceId, { notes });
        }}
        open={!!drawerTarget}
      />
    </>
  );
}
